<template>
  <div ref="plyrContainer" class="plyr-video-wrapper">
    <CldnVideo ref="cldnVideo" v-bind="cldnVideoProps" />
  </div>
</template>

<script setup>
import CldnVideo from '@/components/CldnVideo.vue';

const props = defineProps({
  src: {
    type: String,
    required: true,
  },
  alt: {
    type: String,
    default: '',
  },
  width: {
    type: Number,
    default: 0,
  },
  height: {
    type: Number,
    default: 0,
  },
  ratio: {
    type: [Number, String],
    default: null,
  },
  quality: {
    type: [Number, String],
    default: 'auto',
  },
  crop: {
    type: String,
    default: null,
  },
  autoplay: {
    type: Boolean,
    default: false,
  },
  muted: {
    type: Boolean,
    default: true,
  },
  controls: {
    type: Boolean,
    default: true,
  },
  isThumbnail: {
    type: Boolean,
    default: false,
  },
  playsinline: {
    type: Boolean,
    default: true,
  },

  // Plyr-specific props
  plyrOptions: {
    type: Object,
    default: () => ({}),
  },
  poster: {
    type: [String, Boolean],
    default: true,
  },
});

const emit = defineEmits(['fullscreen-request', 'plyr-ready']);

const plyrContainer = ref(null);
const cldnVideo = ref(null);
const plyrInstance = ref(null);

const { $Plyr, $i18n } = useNuxtApp();

const cldnVideoProps = computed(() => ({
  src: props.src,
  alt: props.alt,
  width: props.width,
  height: props.height,
  ratio: props.ratio,
  quality: props.quality,
  crop: props.crop,
  autoplay: props.autoplay,
  muted: props.muted,
  isThumbnail: props.isThumbnail,
  poster: props.poster,
}));

const plyrConfig = computed(() => ({
  controls: ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'fullscreen'],
  i18n: {
    play: $i18n.t('video.play'),
    pause: $i18n.t('video.pause'),
    restart: $i18n.t('video.restart'),
    rewind: $i18n.t('video.rewind', { seektime: '{seektime}' }),
    fastForward: $i18n.t('video.fastForward', { seektime: '{seektime}' }),
    seek: $i18n.t('video.seek'),
    seekLabel: $i18n.t('video.seekLabel', { currentTime: '{currentTime}', duration: '{duration}' }),
    currentTime: $i18n.t('video.currentTime'),
    duration: $i18n.t('video.duration'),
    volume: $i18n.t('video.volume'),
    mute: $i18n.t('video.mute'),
    unmute: $i18n.t('video.unmute'),
    enterFullscreen: $i18n.t('video.enterFullscreen'),
    exitFullscreen: $i18n.t('video.exitFullscreen'),
  },
  keyboard: {
    focused: true,
    global: false,
  },
  tooltips: {
    controls: true,
    seek: true,
  },
  fullscreen: {
    enabled: true,
    fallback: true,
    iosNative: false,
  },
  ...props.plyrOptions,
}));

const initPlyr = () => {
  try {
    const videoElement = cldnVideo.value?.$refs?.video;

    if (!videoElement) {
      console.error('PlyrVideo: Video element not found');
      return;
    }

    if (plyrInstance.value) {
      return;
    }

    plyrInstance.value = new $Plyr(videoElement, plyrConfig.value);

    plyrInstance.value.on('error', (event) => {
      console.error('PlyrVideo: Playback error', event);

      if (window.$sentry) {
        window.$sentry.captureException(new Error('Plyr playback error'), {
          extra: { src: props.src, event },
        });
      }
    });

    // Intercept fullscreen button clicks
    const fullscreenButton = plyrContainer.value?.querySelector('[data-plyr="fullscreen"]');
    if (fullscreenButton) {
      fullscreenButton.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          emit('fullscreen-request', { shouldAutoplay: true });
        },
        true
      );
    }

    emit('plyr-ready', plyrInstance.value);
  } catch (error) {
    console.error('PlyrVideo: Initialization failed', error);

    if (cldnVideo.value?.$refs?.video) {
      cldnVideo.value.$refs.video.controls = true;
    }

    if (window.$sentry) {
      window.$sentry.captureException(error, {
        extra: { src: props.src },
      });
    }
  }
};

const setupPlyr = (videoElement) => {
  cldnVideo.value = { $refs: { video: videoElement } };

  if (videoElement.readyState >= 1) {
    initPlyr();
  } else {
    videoElement.addEventListener('loadeddata', initPlyr, { once: true });
  }
};

onMounted(() => {
  const videoElement = plyrContainer.value?.querySelector('video');

  if (videoElement) {
    setupPlyr(videoElement);
  } else {
    const observer = new MutationObserver(() => {
      const video = plyrContainer.value?.querySelector('video');
      if (video) {
        observer.disconnect();
        setupPlyr(video);
      }
    });

    if (plyrContainer.value) {
      observer.observe(plyrContainer.value, {
        childList: true,
        subtree: true,
      });
    }
  }
});

// Cleanup
onBeforeUnmount(() => {
  if (plyrInstance.value) {
    plyrInstance.value.destroy();
    plyrInstance.value = null;
  }
});

// Expose Plyr instance and methods to parent
defineExpose({
  plyr: plyrInstance,
  play: () => plyrInstance.value?.play(),
  pause: () => plyrInstance.value?.pause(),
});
</script>

<style scoped>
.plyr-video-wrapper {
  position: relative;
  width: 100%;
}
</style>
